import * as React from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButtonSeeker } from 'ui-seeker/core'
import { monthNames } from 'app/@utils/dateLocale'
import { DAYS_IN_MONTH, toMmdd, isInRange, isClosedDay, formatMmdd, formatPeriod } from 'app/@utils/dayMonthRanges'
import './DayMonthRangesEditor.css'

// Full-year calendar (12 months, no year) to edit day-month ranges (hotel closed periods, business date).
// First click = first day, second click = last day (before the first one = the range crosses the year end).
// Controlled: `ranges` [{start: 'MMDD', end: 'MMDD'}] + `onChange(ranges)`; `onPendingChange(bool)` while a range is half-picked.
export default function DayMonthRangesEditor({ ranges, onChange, onPendingChange, hint, selectedLabel, emptyLabel, note }) {
    const { t } = useTranslation('common')
    const [pendingStart, setPendingStart] = useState(null)
    const [hovered, setHovered] = useState(null)

    const months = useMemo(() => monthNames('long'), [])
    const shortMonths = useMemo(() => monthNames('short'), [])

    const setPending = value => {
        setPendingStart(value)
        if (onPendingChange) onPendingChange(!!value)
    }

    const onDayClick = mmdd => {
        if (!pendingStart) {
            setPending(mmdd)
            return
        }
        onChange([...(ranges || []), { start: pendingStart, end: mmdd }])
        setPending(null)
        setHovered(null)
    }

    const onRemove = index => onChange((ranges || []).filter((_, i) => i !== index))

    const dayClassName = mmdd => {
        const classes = ['day-month-ranges-day']
        if (isClosedDay(mmdd, ranges || [])) classes.push('is-selected')
        if (pendingStart === mmdd) classes.push('is-pending')
        else if (pendingStart && hovered && isInRange(mmdd, pendingStart, hovered)) classes.push('is-preview')
        return classes.join(' ')
    }

    return <div className="day-month-ranges">
        <p className="day-month-ranges-hint">
            {pendingStart ?
                t('common:dayMonthRanges.pendingStart', { date: formatMmdd(pendingStart, shortMonths) }) :
                hint
            }
        </p>

        <div className="day-month-ranges-selected">
            <span className="day-month-ranges-selected-title">{selectedLabel}</span>
            {!ranges?.length ?
                <span className="day-month-ranges-empty">{emptyLabel}</span> :
                <div className="day-month-ranges-chips">
                    {ranges.map((range, index) => <span key={`${range.start}-${range.end}-${index}`} className="day-month-ranges-chip">
                        {formatPeriod(range, shortMonths)}
                        <IconButtonSeeker
                            icon="times"
                            iconType="fontAwesome"
                            className="day-month-ranges-chip-remove"
                            title={t('common:dayMonthRanges.remove')}
                            onClick={() => onRemove(index)}
                        />
                    </span>)}
                </div>
            }
        </div>

        <div className="day-month-ranges-year" onMouseLeave={() => setHovered(null)}>
            {months.map((monthName, monthIndex) => <div key={monthName} className="day-month-ranges-month">
                <div className="day-month-ranges-month-name">{monthName}</div>
                <div className="day-month-ranges-days">
                    {[...Array(DAYS_IN_MONTH[monthIndex]).keys()].map(dayIndex => {
                        const mmdd = toMmdd(monthIndex + 1, dayIndex + 1)
                        return <button
                            key={mmdd}
                            type="button"
                            className={dayClassName(mmdd)}
                            onClick={() => onDayClick(mmdd)}
                            onMouseEnter={() => pendingStart && setHovered(mmdd)}
                        >
                            {dayIndex + 1}
                        </button>
                    })}
                </div>
            </div>)}
        </div>

        {note && <p className="day-month-ranges-note">{note}</p>}
    </div>
}
